import api from './api';

// Course tipləri
export interface Course {
  id: number;
  name: string;
  company: number;
  company_name: string;
  description: string;
  start_date: string | null;
  end_date: string | null;
  price: number | null;
  is_active: boolean;
  subject_count: number;
  student_count: number;
  active_students_count: number;
  duration_days: number | null;
  subjects: CourseSubject[];
  course_students: CourseStudent[];
  created_at: string;
  updated_at: string;
}

export interface CourseSubject {
  id: number;
  specialty: number;
  specialty_name: string;
  specialty_full_path: string;
  teacher: number;
  teacher_name: string;
  hours: number;
  order: number;
  is_active: boolean;
  created_at: string;
}

export interface CourseStudent {
  id: number;
  student: number;
  student_name: string;
  student_fin: string;
  enrollment_date: string;
  start_date: string | null;
  end_date: string | null;
  payment_amount: number | null;
  payment_date: string | null;
  is_active: boolean;
  is_completed: boolean;
  is_paid: boolean;
  duration_days: number | null;
  notes: string;
  created_at: string;
  updated_at: string;
}

export interface CourseCreate {
  name: string;
  company: number;
  description?: string;
  start_date?: string | null;
  end_date?: string | null;
  price?: number | null;
  is_active: boolean;
  subjects?: CourseSubjectCreate[];
  students?: CourseStudentCreate[];
}

export interface CourseSubjectCreate {
  specialty: number;
  teacher: number;
  hours: number;
  order: number;
}

export interface CourseStudentCreate {
  course: number;
  student: number;
  start_date?: string | null;
  end_date?: string | null;
  payment_amount?: number | null;
  payment_date?: string | null;
  is_active: boolean;
  notes?: string;
}

export interface CourseList {
  id: number;
  name: string;
  company_name: string;
  start_date: string | null;
  end_date: string | null;
  price: number | null;
  subject_count: number;
  student_count: number;
  active_students_count: number;
  subjects_names: string[];
  is_active: boolean;
  created_at: string;
}

export interface CourseSelect {
  value: number;
  label: string;
  company: number;
}

export interface CourseFilters {
  search?: string;
  is_active?: boolean;
}

export interface APIResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
  errors?: any;
}

class CourseAPI {
  // Kursları əldə et
  async getCourses(companyId: number, filters?: CourseFilters): Promise<APIResponse<CourseList[]>> {
    try {
      let url = `/courses/?company=${companyId}`;
      
      if (filters?.search) {
        url += `&search=${encodeURIComponent(filters.search)}`;
      }
      
      if (filters?.is_active !== undefined) {
        url += `&is_active=${filters.is_active}`;
      }

      const response = await api.get(url);
      
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      console.error('Kurslar yüklənmədi:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Kurslar yüklənmədi'
      };
    }
  }

  // Tək kurs əldə et
  async getCourse(id: number): Promise<APIResponse<Course>> {
    try {
      const response = await api.get(`/courses/${id}/`);
      
      // Backend {success: true, data: ...} formatında cavab qaytarır
      if (response.data.success) {
        return {
          success: true,
          data: response.data.data
        };
      } else {
        return {
          success: false,
          message: response.data.message || 'Kurs yüklənmədi'
        };
      }
    } catch (error: any) {
      console.error('Kurs yüklənmədi:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Kurs yüklənmədi'
      };
    }
  }

  // Select üçün kurs seçimləri
  async getCourseSelectOptions(companyId: number): Promise<APIResponse<CourseSelect[]>> {
    try {
      const response = await api.get(`/courses/select_options/?company=${companyId}`);
      
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      console.error('Kurs seçimləri yüklənmədi:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Kurs seçimləri yüklənmədi'
      };
    }
  }

  // Yeni kurs yarat
  async createCourse(courseData: CourseCreate): Promise<APIResponse<Course>> {
    try {
      const response = await api.post('/courses/', courseData);
      
      return {
        success: response.data.success,
        message: response.data.message,
        data: response.data.data
      };
    } catch (error: any) {
      console.error('Kurs yaradılmadı:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Kurs yaradılmadı',
        errors: error.response?.data?.errors
      };
    }
  }

  // Kursu yenilə
  async updateCourse(id: number, courseData: Partial<CourseCreate>): Promise<APIResponse<Course>> {
    try {
      const response = await api.put(`/courses/${id}/`, courseData);
      
      return {
        success: response.data.success,
        message: response.data.message,
        data: response.data.data
      };
    } catch (error: any) {
      console.error('Kurs yenilənmədi:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Kurs yenilənmədi',
        errors: error.response?.data?.errors
      };
    }
  }

  // Kursu sil
  async deleteCourse(id: number): Promise<APIResponse<null>> {
    try {
      const response = await api.delete(`/courses/${id}/`);
      
      return {
        success: response.data.success,
        message: response.data.message
      };
    } catch (error: any) {
      console.error('Kurs silinmədi:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Kurs silinmədi'
      };
    }
  }

  // Kurs statistikaları
  async getCourseStatistics(companyId?: number): Promise<APIResponse<any>> {
    try {
      const url = companyId 
        ? `/courses/statistics/?company=${companyId}`
        : '/courses/statistics/';
        
      const response = await api.get(url);
      
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      console.error('Statistikalar yüklənmədi:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Statistikalar yüklənmədi'
      };
    }
  }

  // Kursa tələbə əlavə et
  async addStudentToCourse(courseStudentData: CourseStudentCreate): Promise<APIResponse<CourseStudent>> {
    try {
      const response = await api.post('/course-students/', courseStudentData);
      
      return {
        success: response.data.success,
        message: response.data.message,
        data: response.data.data
      };
    } catch (error: any) {
      console.error('Tələbə kursa əlavə edilmədi:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Tələbə kursa əlavə edilmədi',
        errors: error.response?.data?.errors
      };
    }
  }

  // Tələbəni kursdan çıxar
  async removeStudentFromCourse(courseId: number, studentId: number): Promise<APIResponse<null>> {
    try {
      // Əvvəlcə CourseStudent record-unu tap
      const courseStudentsResponse = await api.get(`/course-students/?course=${courseId}&student=${studentId}`);
      
      if (courseStudentsResponse.data && courseStudentsResponse.data.length > 0) {
        const courseStudentId = courseStudentsResponse.data[0].id;
        const response = await api.delete(`/course-students/${courseStudentId}/`);
        
        return {
          success: response.data.success,
          message: response.data.message
        };
      } else {
        return {
          success: false,
          message: 'Tələbə bu kursda tapılmadı'
        };
      }
    } catch (error: any) {
      console.error('Tələbə kursdan çıxarılmadı:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Tələbə kursdan çıxarılmadı'
      };
    }
  }

  // Kurs tələbəsinin məlumatlarını yenilə
  async updateCourseStudent(courseStudentId: number, courseStudentData: Partial<CourseStudentCreate>): Promise<APIResponse<CourseStudent>> {
    try {
      console.log('updateCourseStudent API - ID:', courseStudentId);
      console.log('updateCourseStudent API - Data:', courseStudentData);
      
      const response = await api.put(`/course-students/${courseStudentId}/`, courseStudentData);
      
      console.log('updateCourseStudent API - Response:', response.data);
      
      return {
        success: response.data.success,
        message: response.data.message,
        data: response.data.data
      };
    } catch (error: any) {
      console.error('Tələbə məlumatları yenilənmədi:', error);
      console.error('Error response:', error.response?.data);
      return {
        success: false,
        message: error.response?.data?.message || 'Tələbə məlumatları yenilənmədi',
        errors: error.response?.data?.errors
      };
    }
  }

  // Kursa fənn əlavə et
  async addSubjectToCourse(courseSubjectData: CourseSubjectCreate & { course: number }): Promise<APIResponse<CourseSubject>> {
    try {
      const response = await api.post('/course-subjects/', courseSubjectData);
      
      return {
        success: response.data.success,
        message: response.data.message,
        data: response.data.data
      };
    } catch (error: any) {
      console.error('Fənn kursa əlavə edilmədi:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Fənn kursa əlavə edilmədi',
        errors: error.response?.data?.errors
      };
    }
  }
}

export const courseAPI = new CourseAPI();